import {
    AhaMomentImage,
    type AhaMomentIntake,
    type AhaResult,
    CMMBranch,
    type MoneyMapNumbers,
    type OnboardingChatResults,
} from '@fruitful/types';

import {
    buildSimulation,
    DEPOSIT_APY_PERCENT,
    HIGH_INTEREST_THRESHOLD,
    INVESTMENT_RETURN_PERCENT,
    MAX_SIMULATION_MONTHS,
} from './builders';
import { runSimulation } from './engine';
import {
    buildInvestmentWaterfall,
    formatDollarsLocal,
} from './investmentWaterfall';
import type { SimulationParams } from './types';
import { formatSaveForGoalLabel } from './withArticle';

/**
 * For goals-system, the aha moment priority is:
 * 1. High-interest debt → debt aha
 * 2. Savings goal → savings aha
 * 3. Low-interest debt → debt aha
 */
const goalsSystemHasHighInterestDebt = (intake: AhaMomentIntake): boolean =>
    (intake.debts ?? []).some(
        (d) =>
            d.annualInterestRatePercent != null &&
            d.annualInterestRatePercent / 100 > HIGH_INTEREST_THRESHOLD,
    );

const MAX_REASONABLE_TARGET_CENTS = 20_000_000; // $200k

/**
 * Pick the savings goal with the highest targetAmountCents that is at or below
 * $200k. If every goal exceeds $200k, pick the lowest instead.
 */
const primarySavingsGoal = (intake: AhaMomentIntake) => {
    const goals = intake.savings;
    if (goals == null || goals.length === 0) return undefined;
    const under = goals.filter(
        (g) => g.targetAmountCents <= MAX_REASONABLE_TARGET_CENTS,
    );
    if (under.length > 0) {
        return under.reduce((best, g) =>
            g.targetAmountCents > best.targetAmountCents ? g : best,
        );
    }
    return goals.reduce((best, g) =>
        g.targetAmountCents < best.targetAmountCents ? g : best,
    );
};

export const isSavingsPath = (rawChatResults: AhaMomentIntake): boolean => {
    if (rawChatResults.branch === CMMBranch.GoalsSystem) {
        return !goalsSystemHasHighInterestDebt(rawChatResults);
    }
    if (
        rawChatResults.branch === CMMBranch.GoalsDebt &&
        (rawChatResults.debts == null || rawChatResults.debts.length === 0)
    ) {
        return true;
    }
    return (
        rawChatResults.branch === CMMBranch.GoalsSavings ||
        (rawChatResults.branch === CMMBranch.MoneySystem &&
            rawChatResults.savings != null)
    );
};

/**
 * Re-derives the overall milestone date (debt-free or goal-funded) from the
 * raw intake using the current spend/goals split. Returns null when the date
 * can't be determined (e.g. goals are unreachable within the simulation window).
 */
function computeDebtMilestoneDate(
    rawChatResults: AhaMomentIntake,
    spendTotalCents: number,
    goalsTotalCents: number,
): string | null {
    const effectiveIntake: AhaMomentIntake = {
        ...rawChatResults,
        monthlySpendCents: spendTotalCents,
        monthlyGoalsSpendCents: goalsTotalCents,
    };

    const simulation = buildSimulation(effectiveIntake);
    if (simulation == null) return null;

    const params: SimulationParams = {
        startDate: new Date(),
        numMonths: MAX_SIMULATION_MONTHS,
        depositApyPercent: DEPOSIT_APY_PERCENT,
        investmentReturnPercent: INVESTMENT_RETURN_PERCENT,
    };

    const result = runSimulation(simulation, params);

    if (isSavingsPath(rawChatResults)) {
        const goal = primarySavingsGoal(rawChatResults);
        if (goal == null) return null;
        for (const month of result.months) {
            if (
                (month.balancesAfter['savings-goal'] ?? 0) >=
                goal.targetAmountCents
            ) {
                return month.date.toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric',
                });
            }
        }
        return null;
    }

    const debtAccountIds = simulation.accounts
        .filter((a) => a.type === 'DEBT')
        .map((a) => a.id);

    for (const month of result.months) {
        const allPaidOff = debtAccountIds.every(
            (id) => (month.balancesAfter[id] ?? 0) <= 0,
        );
        if (allPaidOff) {
            return month.date.toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric',
            });
        }
    }

    return null;
}

/**
 * Derive the display headline purely from intake data, the computed
 * goalsTotalCents, and whether a milestone date was found.
 */
export function computeDisplayHeadline(
    rawChatResults: AhaMomentIntake,
    goalsTotalCents: number,
    hasDate: boolean,
): string {
    if (
        rawChatResults.branch === CMMBranch.StartInvesting &&
        rawChatResults.investment != null
    ) {
        const waterfall = buildInvestmentWaterfall(rawChatResults.investment);
        return `${formatDollarsLocal(waterfall.projectedTotalCents / 100, false)} invested by`;
    }

    if (isSavingsPath(rawChatResults)) {
        const goalPhrase = formatSaveForGoalLabel(
            primarySavingsGoal(rawChatResults)?.description,
        );
        return hasDate ? `${goalPhrase} by` : goalPhrase;
    }

    const hasHighInterest = (rawChatResults.debts ?? []).some(
        (d) =>
            d.annualInterestRatePercent != null &&
            d.annualInterestRatePercent / 100 > HIGH_INTEREST_THRESHOLD,
    );
    const debtGoalName = hasHighInterest
        ? 'Pay off high-interest debt'
        : 'Pay off debt';

    if (goalsTotalCents <= 0) return debtGoalName;

    return hasDate ? 'Debt free by' : debtGoalName;
}

function buildMoneyMapNumbers(results: OnboardingChatResults): MoneyMapNumbers {
    const { moneyMap } = results;
    return {
        incomeTotalCents: Number(moneyMap.incomeTotalCents) || 0,
        billsTotalCents: Number(moneyMap.billsTotalCents) || 0,
        spendTotalCents: Number(moneyMap.spendTotalCents) || 0,
        goalsTotalCents: Number(moneyMap.goalsTotalCents) || 0,
    };
}

export function buildAhaResult(results: OnboardingChatResults): AhaResult {
    const { rawChatResults, artifactData, highInterestDebtTotalCents } =
        results;
    const image = artifactData.image ?? AhaMomentImage.Management;
    const moneyMap = buildMoneyMapNumbers(results);

    if (
        rawChatResults?.branch === CMMBranch.StartInvesting &&
        rawChatResults.investment != null
    ) {
        const waterfall = buildInvestmentWaterfall(rawChatResults.investment);
        return {
            type: 'investing',
            displayHeadline: `${formatDollarsLocal(waterfall.projectedTotalCents / 100, false)} invested by`,
            image,
            moneyMap,
            projectedTotalCents: waterfall.projectedTotalCents,
            projectedDate: waterfall.projectedDate,
        };
    }

    const { spendTotalCents, goalsTotalCents } = moneyMap;
    const initialDebtFreeDate = artifactData.debtFreeDate ?? '';
    const debtFreeDate =
        rawChatResults == null
            ? initialDebtFreeDate
            : (computeDebtMilestoneDate(
                  rawChatResults,
                  spendTotalCents,
                  goalsTotalCents,
              ) ?? initialDebtFreeDate);

    const displayHeadline =
        rawChatResults == null
            ? 'Your plan'
            : computeDisplayHeadline(
                  rawChatResults,
                  goalsTotalCents,
                  debtFreeDate !== '',
              );

    if (rawChatResults != null && isSavingsPath(rawChatResults)) {
        return {
            type: 'savings',
            displayHeadline,
            image,
            moneyMap,
            debtFreeDate,
        };
    }

    return {
        type: 'debt',
        displayHeadline,
        image,
        moneyMap,
        debtFreeDate,
        highInterestDebtTotalCents: highInterestDebtTotalCents ?? undefined,
    };
}
